import { useMemo, useState } from "react";

import { projectLabelFromId } from "../app/format";
import {
  FacetFilterBar,
  labelForFacetValue,
  riskGroupByOptions,
  toneForFacetValue,
  useFacetFilter,
  type UseFacetFilterConfig
} from "../components/facets";
import type {
  RiskLedgerAssumptionDisplay,
  RiskLedgerClarificationDisplay,
  RiskLedgerDisplayViewModel,
  RiskLedgerExclusionDisplay,
  RiskLedgerRiskDisplay,
  RiskLedgerSectionId,
  RiskLedgerSectionSummary
} from "../viewmodel/riskLedgerAdapter";
import { AiProvenanceBadge, cx } from "./shared";

type LedgerRow = {
  id: string;
  sectionId: RiskLedgerSectionId;
  accent: string;
  tag: string;
  tagClassName: string;
  title: string;
  message: string;
  affectedLabel: string;
  sourceItemCount: number;
  sourceRefCount: number;
  ruleTraceRefCount: number;
  blocksExport: boolean;
  provenanceWhy: string;
  provenanceWhere: string;
  searchText: string;
};

type LedgerGroup = Omit<RiskLedgerSectionSummary, "id"> & {
  id: string;
  emptyText: string;
  rows: LedgerRow[];
};

const riskSeverityTone = {
  blocker: {
    tag: "border-[#E9C7C0] bg-[#F6E7E4] text-[#BC4A3C]",
    accent: "#BC4A3C"
  },
  warning: {
    tag: "border-[#E9D3AD] bg-[#F6EEDD] text-[#8A6A2E]",
    accent: "#A8823E"
  },
  info: {
    tag: "border-[#D7E1EA] bg-[#E2ECF4] text-[#3E6E9A]",
    accent: "#3E6E9A"
  }
} as const;

const sectionTone: Record<
  Exclude<RiskLedgerSectionId, "risks">,
  { tag: string; accent: string }
> = {
  assumptions: {
    tag: "border-[#DCE2E8] bg-[#ECEFF3] text-[#5E6E82]",
    accent: "#5E6E82"
  },
  clarifications: {
    tag: "border-[#DDD8EE] bg-[#ECEAF6] text-[#6A5E97]",
    accent: "#6A5E97"
  },
  exclusions: {
    tag: "border-[#EAD9B9] bg-[#F3E8D4] text-[#8A6A2E]",
    accent: "#B08A3E"
  }
};

const fallbackSummaries: Record<RiskLedgerSectionId, RiskLedgerSectionSummary> = {
  risks: {
    id: "risks",
    label: "Risks",
    count: 0,
    sourceItemCount: 0,
    accent: "#BC4A3C"
  },
  assumptions: {
    id: "assumptions",
    label: "Assumptions",
    count: 0,
    sourceItemCount: 0,
    accent: "#5E6E82"
  },
  clarifications: {
    id: "clarifications",
    label: "Clarifications",
    count: 0,
    sourceItemCount: 0,
    accent: "#6A5E97"
  },
  exclusions: {
    id: "exclusions",
    label: "Exclusions",
    count: 0,
    sourceItemCount: 0,
    accent: "#B08A3E"
  }
};

const groupOrder: RiskLedgerSectionId[] = [
  "risks",
  "assumptions",
  "clarifications",
  "exclusions"
];

const compactCountLabel = (rowCount: number, sourceItemCount: number): string =>
  sourceItemCount > rowCount ? `${rowCount} from ${sourceItemCount}` : String(rowCount);

const repeatedLabel = (count: number): string | undefined =>
  count > 1 ? `×${count}` : undefined;

const searchText = (...parts: readonly (string | number | undefined)[]): string =>
  parts.filter(Boolean).join(" ").toLowerCase();

const provenanceWhereLabel = (
  sourceRefCount: number,
  ruleTraceRefCount: number
): string => {
  const parts = [
    sourceRefCount > 0
      ? `${sourceRefCount} source${sourceRefCount === 1 ? "" : "s"}`
      : undefined,
    ruleTraceRefCount > 0
      ? `${ruleTraceRefCount} check${ruleTraceRefCount === 1 ? "" : "s"}`
      : undefined
  ].filter((part): part is string => part !== undefined);

  return parts.length > 0
    ? `Where: ${parts.join(" and ")} attached to this ledger row.`
    : "Where: no source trail is available in this view.";
};

const riskFacetDefs: UseFacetFilterConfig<RiskLedgerRiskDisplay>["facetDefs"] = [
  {
    key: "severity",
    label: "Severity",
    valueOf: (risk) => risk.facets.severity,
    toneOf: (value) => toneForFacetValue("severity", value)
  },
  {
    key: "system",
    label: "System",
    valueOf: (risk) => risk.facets.system,
    toneOf: (value) => toneForFacetValue("system", value)
  },
  {
    key: "nature",
    label: "Nature",
    valueOf: (risk) => risk.facets.nature,
    toneOf: (value) => toneForFacetValue("nature", value)
  },
  {
    key: "owner",
    label: "Owner",
    valueOf: (risk) => risk.facets.actionOwner,
    toneOf: (value) => toneForFacetValue("owner", value)
  },
  {
    key: "status",
    label: "Resolution",
    valueOf: (risk) => risk.facets.resolution,
    toneOf: (value) => toneForFacetValue("status", value)
  }
];

const riskScreenGroupByOptions = [
  { value: null, label: "Section" },
  ...riskGroupByOptions
    .filter((option) => option.value === "severity" || option.value === "system")
    .map((option) => ({ value: option.value, label: option.label }))
];

const labelForRiskFacetValue = (facetKey: string, value: string): string =>
  labelForFacetValue(facetKey, value);

const optionalFacetLabel = (
  facetKey: string,
  value: string | undefined
): string | undefined => (value ? labelForRiskFacetValue(facetKey, value) : undefined);

const riskSearchText = (risk: RiskLedgerRiskDisplay): string =>
  searchText(
    risk.id,
    risk.title,
    risk.message,
    risk.typeLabel,
    risk.statusLabel,
    risk.affectedLabel,
    optionalFacetLabel("severity", risk.facets.severity),
    optionalFacetLabel("system", risk.facets.system),
    optionalFacetLabel("nature", risk.facets.nature),
    optionalFacetLabel("owner", risk.facets.actionOwner),
    optionalFacetLabel("status", risk.facets.resolution),
    risk.actionOptions.map((option) => option.action).join(" ")
  );

const riskToRow = (risk: RiskLedgerRiskDisplay): LedgerRow => {
  const tone = riskSeverityTone[risk.severity];

  return {
    id: `risk-${risk.id}`,
    sectionId: "risks",
    accent: tone.accent,
    tag: `Risk · ${risk.severityLabel}`,
    tagClassName: tone.tag,
    title: risk.title,
    message: risk.message,
    affectedLabel: risk.affectedLabel,
    sourceItemCount: risk.sourceItemCount,
    sourceRefCount: risk.sourceRefCount,
    ruleTraceRefCount: risk.ruleTraceRefCount,
    blocksExport: risk.blocksExport,
    provenanceWhy: "AI-produced risk item from the generated risk ledger.",
    provenanceWhere: provenanceWhereLabel(risk.sourceRefCount, risk.ruleTraceRefCount),
    searchText: riskSearchText(risk)
  };
};

const assumptionToRow = (assumption: RiskLedgerAssumptionDisplay): LedgerRow => ({
  id: `assumption-${assumption.id}`,
  sectionId: "assumptions",
  accent: sectionTone.assumptions.accent,
  tag: "Assumption",
  tagClassName: sectionTone.assumptions.tag,
  title: assumption.text,
  message:
    assumption.targetLabel === "Multiple targets"
      ? "Applies to multiple generated targets."
      : `Target: ${assumption.targetLabel}.`,
  affectedLabel: assumption.affectedLabel,
  sourceItemCount: assumption.sourceItemCount,
  sourceRefCount: assumption.sourceRefCount,
  ruleTraceRefCount: assumption.ruleTraceRefCount,
  blocksExport: false,
  provenanceWhy:
    "AI-produced assumption retained from the specification justification.",
  provenanceWhere: provenanceWhereLabel(
    assumption.sourceRefCount,
    assumption.ruleTraceRefCount
  ),
  searchText: searchText(
    assumption.id,
    assumption.text,
    assumption.targetLabel,
    assumption.affectedLabel
  )
});

const clarificationToRow = (
  clarification: RiskLedgerClarificationDisplay
): LedgerRow => ({
  id: `clarification-${clarification.id}`,
  sectionId: "clarifications",
  accent: sectionTone.clarifications.accent,
  tag: "Clarification",
  tagClassName: sectionTone.clarifications.tag,
  title: clarification.title,
  message:
    clarification.question === clarification.title
      ? "Internal follow-up wording is ready for review."
      : clarification.question,
  affectedLabel: clarification.affectedLabel,
  sourceItemCount: clarification.sourceItemCount,
  sourceRefCount: 0,
  ruleTraceRefCount: 0,
  blocksExport: false,
  provenanceWhy:
    clarification.source === "needsUserDecision"
      ? "AI-produced internal clarification linked to a decision that needs review."
      : "AI-produced internal clarification from the open decisions queue.",
  provenanceWhere: "Where: no source trail is available in this view.",
  searchText: searchText(
    clarification.id,
    clarification.title,
    clarification.question,
    clarification.affectedLabel,
    clarification.stageLabel
  )
});

const exclusionToRow = (exclusion: RiskLedgerExclusionDisplay): LedgerRow => ({
  id: `exclusion-${exclusion.id}`,
  sectionId: "exclusions",
  accent: sectionTone.exclusions.accent,
  tag: "Exclusion",
  tagClassName: sectionTone.exclusions.tag,
  title: exclusion.title,
  message: exclusion.message,
  affectedLabel: exclusion.affectedLabel,
  sourceItemCount: exclusion.sourceItemCount,
  sourceRefCount: exclusion.sourceRefCount,
  ruleTraceRefCount: exclusion.ruleTraceRefCount,
  blocksExport: false,
  provenanceWhy:
    "AI-produced exclusion or by-others row from the generated scope ledger.",
  provenanceWhere: provenanceWhereLabel(
    exclusion.sourceRefCount,
    exclusion.ruleTraceRefCount
  ),
  searchText: searchText(
    exclusion.id,
    exclusion.title,
    exclusion.message,
    exclusion.affectedLabel,
    exclusion.statusLabel
  )
});

const sectionRows = (
  view: RiskLedgerDisplayViewModel,
  riskRows: readonly LedgerRow[]
): Record<RiskLedgerSectionId, LedgerRow[]> => ({
  risks: [...riskRows],
  assumptions: view.assumptions.map(assumptionToRow),
  clarifications: view.clarifications.map(clarificationToRow),
  exclusions: view.exclusions.map(exclusionToRow)
});

const emptyText: Record<RiskLedgerSectionId, string> = {
  risks: "No risk rows are in this client ledger.",
  assumptions: "No working assumptions are in this client ledger.",
  clarifications: "No clarification prompts are waiting for review.",
  exclusions: "No exclusions are in this client ledger."
};

const checkboxClassName =
  "flex h-[19px] w-[19px] items-center justify-center rounded-[6px] border text-[12px] font-extrabold";

export function RisksScreen({ view }: { view: RiskLedgerDisplayViewModel }) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(
    () => new Set()
  );
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const projectLabel = projectLabelFromId(view.projectId);
  const query = search.trim().toLowerCase();
  const searchedRisks = useMemo(
    () =>
      query
        ? view.risks.filter((risk) => riskSearchText(risk).includes(query))
        : view.risks,
    [query, view.risks]
  );

  const riskFacetFilter = useFacetFilter({
    items: searchedRisks,
    facetDefs: riskFacetDefs,
    labelFor: labelForRiskFacetValue,
    initialGroupBy: null
  });
  const hasRiskFacetSelection = Object.values(riskFacetFilter.selection).some(
    (values) => values.length > 0
  );

  const groups = useMemo<LedgerGroup[]>(() => {
    if (
      riskFacetFilter.groupBy === "severity" ||
      riskFacetFilter.groupBy === "system"
    ) {
      const facetGroups = riskFacetFilter.groups.map((group) => {
        const rows = group.items.map(riskToRow);
        const accent =
          riskFacetFilter.groupBy === "severity" && group.key in riskSeverityTone
            ? riskSeverityTone[group.key as keyof typeof riskSeverityTone].accent
            : "#5E6E82";

        return {
          id: `${riskFacetFilter.groupBy}-${group.key}`,
          label: group.label,
          count: rows.length,
          sourceItemCount: rows.reduce((sum, row) => sum + row.sourceItemCount, 0),
          accent,
          emptyText: "No risks match these filters.",
          rows
        };
      });

      return facetGroups.length > 0
        ? facetGroups
        : [
            {
              id: `${riskFacetFilter.groupBy}-empty`,
              label: "No risks",
              count: 0,
              sourceItemCount: 0,
              accent: "#5E6E82",
              emptyText: "No risks match these filters.",
              rows: []
            }
          ];
    }

    const summaryById = new Map(
      view.ledgerGroups.map((summary) => [summary.id, summary])
    );
    const rowsBySection = sectionRows(view, riskFacetFilter.filtered.map(riskToRow));
    const hideEmptyGroups = !!query || hasRiskFacetSelection;

    return groupOrder
      .map((id) => {
        const rows = query
          ? rowsBySection[id].filter((row) => row.searchText.includes(query))
          : rowsBySection[id];
        const summary = summaryById.get(id) ?? fallbackSummaries[id];

        return {
          ...summary,
          count: rows.length,
          sourceItemCount: rows.reduce((sum, row) => sum + row.sourceItemCount, 0),
          emptyText: emptyText[id],
          rows
        };
      })
      .filter((group) => !hideEmptyGroups || group.rows.length > 0);
  }, [hasRiskFacetSelection, query, riskFacetFilter, view]);

  const visibleRows = groups.flatMap((group) => group.rows);
  const visibleIds = visibleRows.map((row) => row.id);
  const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const setRowsSelected = (ids: readonly string[], selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);

      ids.forEach((id) => {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });

      return next;
    });
  };

  const toggleAllVisible = () => {
    setRowsSelected(visibleIds, !allVisibleSelected);
  };

  const toggleGroup = (rows: readonly LedgerRow[]) => {
    const ids = rows.map((row) => row.id);
    const selectedCount = ids.filter((id) => selectedIds.has(id)).length;

    setRowsSelected(ids, selectedCount !== ids.length);
  };

  const toggleGroupCollapsed = (groupId: string) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);

      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  };

  const filteredRowCount = visibleRows.length;
  const rawItemCount = groups.reduce((sum, group) => sum + group.sourceItemCount, 0);

  return (
    <div className="flex-1 overflow-auto px-8 py-[38px] pb-[72px] xl:px-14">
      <div className="mx-auto max-w-[1080px]">
        <div className="mb-[18px] flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
              {projectLabel} · whole job
            </div>
            <h1 className="m-0 mt-1 text-[26px] font-extrabold tracking-[-.03em]">
              Risks, assumptions & clarifications
            </h1>
            <p className="m-0 mt-2 max-w-[560px] text-[13.5px] leading-[1.5] text-[var(--muted)]">
              Run straight down the list, tick what you're happy with and approve in
              bulk. Each is tagged to the type it affects.
            </p>
          </div>
        </div>

        <div className="mb-[14px]">
          <FacetFilterBar
            facets={riskFacetFilter.facets}
            selection={riskFacetFilter.selection}
            onToggle={riskFacetFilter.toggle}
            onClearAll={riskFacetFilter.clearAll}
            groupBy={riskFacetFilter.groupBy}
            groupByOptions={riskScreenGroupByOptions}
            onGroupByChange={riskFacetFilter.setGroupBy}
          />
        </div>

        <div
          data-showme="risk-review-controls"
          className="mb-[14px] flex flex-wrap items-center gap-[11px] rounded-[11px] border border-[var(--line)] bg-[var(--surface)] px-[14px] py-[9px]"
        >
          <button
            type="button"
            onClick={toggleAllVisible}
            className="flex items-center gap-2"
          >
            <span
              className={cx(
                checkboxClassName,
                allVisibleSelected
                  ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
                  : "border-[var(--line3)] bg-[var(--surface)] text-transparent"
              )}
            >
              ✓
            </span>
            <span className="text-[12.5px] font-bold text-[var(--muted)]">
              Select all
            </span>
          </button>
          <span className="text-[12px] font-semibold text-[var(--faint)]">
            {selectedVisibleCount} selected
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search..."
            className="ml-2 w-[150px] rounded-[9px] border border-[var(--line3)] bg-[var(--surface)] px-3 py-[7px] text-[12.5px] font-semibold text-[var(--ink)] outline-none focus:border-[var(--ink)]"
          />
          <span className="text-[11.5px] font-bold text-[var(--faint)]">
            {compactCountLabel(filteredRowCount, rawItemCount)} rows
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => setSearch("")}
              className="rounded-[9px] border border-[var(--line3)] px-[13px] py-2 text-[12.5px] font-bold text-[var(--muted)]"
            >
              Clear
            </button>
            <button
              type="button"
              className="rounded-[9px] bg-[var(--ink)] px-4 py-2 text-[12.5px] font-bold text-[var(--onSolid)]"
            >
              Approve selected
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-[22px]">
          {groups.map((group) => {
            const groupIds = group.rows.map((row) => row.id);
            const groupSelectedCount = groupIds.filter((id) =>
              selectedIds.has(id)
            ).length;
            const allGroupSelected =
              groupIds.length > 0 && groupSelectedCount === groupIds.length;
            const collapsed = collapsedGroupIds.has(group.id);

            return (
              <section key={group.id}>
                <div className="mb-[9px] flex flex-wrap items-center gap-2.5 px-0.5">
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapsed(group.id)}
                    aria-expanded={!collapsed}
                    className="flex items-center gap-2"
                  >
                    <span className="flex h-[17px] w-[17px] items-center justify-center rounded-[5px] border border-[var(--line3)] bg-[var(--surface)] text-[11px] font-extrabold text-[var(--muted)]">
                      {collapsed ? "+" : "-"}
                    </span>
                    <span className="text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
                      {group.label} ·{" "}
                      {compactCountLabel(group.count, group.sourceItemCount)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.rows)}
                    title={`Select all in ${group.label}`}
                    disabled={group.rows.length === 0}
                    className="flex items-center gap-1.5 disabled:opacity-45"
                  >
                    <span
                      className={cx(
                        "flex h-[17px] w-[17px] items-center justify-center rounded-[5px] border text-[11px] font-extrabold",
                        allGroupSelected
                          ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
                          : "border-[var(--line3)] bg-[var(--surface)] text-transparent"
                      )}
                    >
                      ✓
                    </span>
                    <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                      Select group
                    </span>
                  </button>
                </div>

                {collapsed ? null : (
                  <div className="overflow-hidden rounded-[13px] border border-[var(--line)] bg-[var(--surface)]">
                    {group.rows.length > 0 ? (
                      group.rows.map((row) => {
                        const selected = selectedIds.has(row.id);
                        const duplicateLabel = repeatedLabel(row.sourceItemCount);

                        return (
                          <article
                            key={row.id}
                            className="border-t border-[var(--line2)] px-4 py-3"
                            style={{ borderLeft: `3px solid ${row.accent}` }}
                          >
                            <div className="flex items-start gap-[13px]">
                              <button
                                type="button"
                                onClick={() => setRowsSelected([row.id], !selected)}
                                className="mt-0.5 flex-none"
                                aria-label={`Select ${row.title}`}
                              >
                                <span
                                  className={cx(
                                    checkboxClassName,
                                    selected
                                      ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
                                      : "border-[var(--line3)] bg-[var(--surface)] text-transparent"
                                  )}
                                >
                                  ✓
                                </span>
                              </button>
                              <span
                                className={cx(
                                  "mt-0.5 flex-none whitespace-nowrap rounded-full border px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-[.05em]",
                                  row.tagClassName
                                )}
                              >
                                {row.tag}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h2 className="m-0 text-[13.5px] font-extrabold leading-[1.35] tracking-[-.01em] text-[var(--ink)]">
                                    {row.title}
                                  </h2>
                                  {duplicateLabel ? (
                                    <span className="rounded-[6px] bg-[var(--chip)] px-2 py-0.5 text-[10px] font-extrabold text-[var(--muted)]">
                                      {duplicateLabel}
                                    </span>
                                  ) : null}
                                  {row.blocksExport ? (
                                    <span className="rounded-[6px] border border-[#E9C7C0] bg-[#F6E7E4] px-2 py-0.5 text-[10px] font-extrabold text-[#BC4A3C]">
                                      Blocks price
                                    </span>
                                  ) : null}
                                </div>
                                <p className="m-0 mt-1 text-[12px] leading-[1.45] text-[var(--muted2)]">
                                  {row.message}
                                </p>
                                <div className="mt-2">
                                  <AiProvenanceBadge
                                    why={row.provenanceWhy}
                                    where={row.provenanceWhere}
                                    compact={
                                      row.sourceRefCount + row.ruleTraceRefCount > 0
                                    }
                                  />
                                </div>
                              </div>
                              <span className="mt-1 flex-none text-[11px] font-semibold text-[var(--faint)]">
                                {row.affectedLabel}
                              </span>
                              <button
                                type="button"
                                className="mt-0.5 flex-none rounded-full border border-[var(--line3)] bg-[var(--surface)] px-3 py-1 text-[11.5px] font-bold text-[var(--ink2)]"
                              >
                                Approve
                              </button>
                            </div>

                            <div className="ml-[45px] mt-[7px] flex items-center gap-2">
                              <span className="flex-none text-[9.5px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                                Note
                              </span>
                              <input
                                value={noteDrafts[row.id] ?? ""}
                                onChange={(event) =>
                                  setNoteDrafts((current) => ({
                                    ...current,
                                    [row.id]: event.currentTarget.value
                                  }))
                                }
                                placeholder="Add a note..."
                                className="min-w-0 flex-1 border-0 border-b border-dashed border-[var(--line3)] bg-transparent px-0.5 pb-1 pt-0.5 text-[12px] text-[var(--ink2)] outline-none focus:border-[var(--ink)]"
                              />
                            </div>
                          </article>
                        );
                      })
                    ) : (
                      <div className="px-4 py-4 text-[12px] font-semibold text-[var(--muted2)]">
                        {group.emptyText}
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="mt-[18px] flex justify-end">
          <button
            type="button"
            className="rounded-[10px] bg-[var(--ink)] px-[18px] py-[11px] text-[13px] font-bold text-[var(--onSolid)]"
          >
            Send approved clarifications to client
          </button>
        </div>
      </div>
    </div>
  );
}
